import { Injectable } from '@angular/core';

import cloneDeep from 'lodash/cloneDeep';
import { CountryModel } from '../shared/models/CountryModel';
import BankAccountFieldType from '../shared/components/client-bank-account/BankAccountFieldType.enum';

export class FieldSetModel {
  public fields: FieldModel[];
}

export class FieldModel {
  public id: number;
  public name: string;
  public fieldType: BankAccountFieldType;
  public required: boolean;
  public urls: any[];
  public patterns: PatternModel[];
}

export class PatternModel {
  public regex: string;
  public errorMessage: string;
}

@Injectable({
  providedIn: 'root'
})
export class ClientBankAccountValidationService {
  private REQUIRED_PROPERTY_NAME = 'Required';
  private SWIFT_FIELD_NAME = 'swift';
  private SWIFT_COUNTRY_PLACEHOLDER = '{0}';
  private IBAN_FIELD_NAME = 'IBAN';
  private IBAN_LENGTH_FIELD = 'IBANLength';
  private IBANASCIIDifference = 55;
  private IBAN_MOD = 97;
  private MAX_DIGITS_VALUE = 20;
  private IBAN_MAX_LENGTH = 100;
  private IBAN_MOD_MAX_LENGTH = 9;
  private US_ABA_ROUTING_NUMBER_FIELD_NAME = 'USABARoutingNumber';
  private US_ABA_ROUTING_NUMBER_LENGTH = 9;
  private DIGITS_ONLY_REGEX = /^\d+$/;
  private OTHER_BANK_CODE = 999;

  private bankAccountFields: FieldSetModel[];
  private currentCountry: CountryModel;
  constructor() {}

  public setCountry(country: CountryModel) {
    this.currentCountry = country;
  }

  public setBankAccountFields(fields: FieldSetModel[]) {
    this.bankAccountFields = fields;
  }

  public getBankAccountFields(): FieldSetModel[] {
    return this.bankAccountFields;
  }

  public validateSwiftField(fieldValue: string): FieldModel {
    let currentField = new FieldModel();
    this.bankAccountFields.filter(fieldsSet => {
      fieldsSet.fields.filter(f => {
        if (f.name.toLowerCase() === this.SWIFT_FIELD_NAME.toLowerCase()) {
          currentField = cloneDeep(f);
        }
      });
    });
    if (currentField !== null && currentField !== undefined) {
      // check if the field answers the required constraint
      if (currentField.required === true && this.isNullOrEmpty(fieldValue) === false) {
        delete currentField.required;
      }
      // validate pattern
      if (
        currentField.patterns !== null &&
        currentField.patterns !== undefined &&
        currentField.patterns.length === 1 &&
        this.currentCountry !== null &&
        this.currentCountry !== undefined &&
        this.isNullOrEmpty(this.currentCountry.code) === false
      ) {
        let currentSwiftRegex = currentField.patterns[0].regex.replace(this.SWIFT_COUNTRY_PLACEHOLDER, this.currentCountry.code);
        currentSwiftRegex = this.formatDoubleBrackets(currentSwiftRegex);
        const testResult = new RegExp(currentSwiftRegex).test(fieldValue ? fieldValue.toUpperCase() : '');
        if (testResult === true) {
          currentField.patterns.splice(0, 1);
        }
      }
      if (currentField.patterns.length === 0) {
        delete currentField.patterns;
      }
      if (currentField.urls.length === 0) {
        delete currentField.urls;
      }
    }
    return currentField;
  }

  public validateField(fieldName: string, fieldValue: string): FieldModel {
    let currentField = new FieldModel();
    this.bankAccountFields.filter(fieldsSet => {
      fieldsSet.fields.filter(f => {
        if (f.name.toLowerCase() === fieldName.toLowerCase()) {
          currentField = cloneDeep(f);
        }
      });
    });

    if (currentField !== null && currentField !== undefined) {
      // check if the field answers the required constraint
      if (currentField.required === true && this.isNullOrEmpty(fieldValue) === false) {
        delete currentField.required;
      }
      // validate patterns
      if (currentField.patterns !== null && currentField.patterns !== undefined && currentField.patterns.length > 0) {
        currentField.patterns.forEach(pattern => {
          const strippedRegex = this.formatDoubleBrackets(pattern.regex);
          const testResult = new RegExp(strippedRegex).test(fieldValue);
          if (testResult === true) {
            currentField.patterns.splice(currentField.patterns.indexOf(pattern), 1);
          }
        });
      }
      if (!currentField.patterns || currentField.patterns.length === 0) {
        delete currentField.patterns;
      }
      if (!currentField.urls || currentField.urls.length === 0) {
        delete currentField.urls;
      }

      return currentField;
    }
  }

  public validateUSABARoutingNumberField(fieldValue) {
    let currentField = new FieldModel();
    this.bankAccountFields.filter(fieldsSet => {
      fieldsSet.fields.filter(f => {
        if (f.name.toLowerCase() === this.US_ABA_ROUTING_NUMBER_FIELD_NAME.toLowerCase()) {
          currentField = cloneDeep(f);
        }
      });
    });
    if (currentField !== null && currentField !== undefined) {
      // check if the field answers the required constraint
      if (currentField.required === true && this.isNullOrEmpty(fieldValue) === false) {
        delete currentField.required;
        let result = 0;
        // check the length
        if (fieldValue.length === this.US_ABA_ROUTING_NUMBER_LENGTH) {
          const digitsOnlyRegexResult = new RegExp(this.DIGITS_ONLY_REGEX).test(fieldValue);
          if (digitsOnlyRegexResult === true) {
            for (let i = 0; i < fieldValue.length; i += 3) {
              result += parseInt(fieldValue.charAt(i), 10) * 3 + parseInt(fieldValue.charAt(i + 1), 10) * 7 + parseInt(fieldValue.charAt(i + 2), 10);
            }
          }
          if (result !== 0 && result % 10 === 0) {
            delete currentField.patterns;
            delete currentField.urls;
          }
        }
      }
    }
    return currentField;
  }

  public validateIBANField(fieldValue, length) {
    // i.e: GB82 WEST 1234 5698 7654 32
    let currentField = new FieldModel();
    this.bankAccountFields.filter(fieldsSet => {
      fieldsSet.fields.filter(f => {
        if (f.name.toLowerCase() === this.IBAN_FIELD_NAME.toLowerCase()) {
          currentField = cloneDeep(f);
        }
      });
    });
    if (currentField !== null && currentField !== undefined) {
      // check if the field answers the required constraint
      if (currentField.required === true && this.isNullOrEmpty(fieldValue) === false) {
        delete currentField.required;
        if (
          this.isNullOrEmpty(length) === false &&
          this.currentCountry !== null &&
          this.currentCountry !== undefined &&
          this.isNullOrEmpty(this.currentCountry.code) === false
        ) {
          let result = -1;
          // the >= is because the ibanLength is the length without the spaces
          if (fieldValue.length >= length) {
            // take the first four letters and move them to the end of the string
            const initialsWithChkDigit = fieldValue.substring(0, 4);
            const initials = initialsWithChkDigit.substring(0, 2);
            if (this.currentCountry.code.toLowerCase() === initials.toLowerCase()) {
              // validate that the check digit is between 1-99
              const checkDigit = initialsWithChkDigit.substring(2);
              const parseCheckDigits = parseInt(checkDigit, 10);
              if (isNaN(parseCheckDigits) === false) {
                if (parseCheckDigits > 1 && parseCheckDigits < 100) {
                  fieldValue = fieldValue.substring(4); // move first four letters to the end of the iban
                  // WEST 1234 5698 7654 32GB82
                  fieldValue += initialsWithChkDigit;
                  // remove spaces = WEST12345698765432GB82
                  fieldValue = fieldValue.replace(/\s/g, '');
                  // convert each letter to its numeric representation A=10, Z=35
                  let convertionResult = '';
                  // tslint:disable-next-line:forin
                  for (let i = 0; i < fieldValue.length; i++) {
                    let parseResult = parseInt(fieldValue[i], 10);
                    if (isNaN(parseResult) === true) {
                      parseResult = fieldValue[i].charCodeAt(0);
                      if (isNaN(parseResult) === false) {
                        parseResult = fieldValue[i].charCodeAt(0) - this.IBANASCIIDifference;
                      }
                    }
                    convertionResult += parseResult + '';
                  }
                  // javascript can parseInt a number of max 21 digit
                  if (convertionResult.length < this.IBAN_MAX_LENGTH) {
                    let tempConversionResult = convertionResult;
                    while (tempConversionResult.length > this.IBAN_MOD_MAX_LENGTH) {
                      const firstNineDigits = tempConversionResult.substring(0, 9);
                      let parseFirstNineDigits = parseInt(firstNineDigits, 10);
                      if (isNaN(parseFirstNineDigits) === false) {
                        parseFirstNineDigits %= this.IBAN_MOD;
                        tempConversionResult = tempConversionResult.substring(this.IBAN_MOD_MAX_LENGTH);
                        tempConversionResult = parseFirstNineDigits + '' + tempConversionResult;
                      }
                    }
                    const parseConvResult = parseInt(tempConversionResult, 10);
                    if (isNaN(parseConvResult) === false) {
                      result = parseConvResult % this.IBAN_MOD;
                    }
                  }
                }
              }
            }
          }
          if (result === 1) {
            delete currentField.patterns;
            delete currentField.urls;
          }
        }
      }
    }

    return currentField;
  }

  public getRegex(pattern: string): RegExp {
    return new RegExp(this.formatDoubleBrackets(pattern));
  }

  private formatDoubleBrackets(value): string {
    return value.replace(/{{/g, '{').replace(/}}/g, '}');
  }

  private isNullOrEmpty(fieldValue) {
    return fieldValue === null || fieldValue === undefined || fieldValue.length === 0;
  }
}
